import { useLazyQuery } from "@apollo/client";
import { IonProgressBar, IonText } from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PieChart } from "react-minimal-pie-chart";
import { useLocation } from "react-router";

import ChartKey from "@components/ChartKey";
import ExpertHighlightsTable from "@components/expert/ExpertHighlightsTable";
import styles from "@components/expert/ExpertKPIs.module.css";
import ExpertKPIsFilters from "@components/forms/ExpertKPIsFilters";
import HandleQuery from "@components/HandleQuery";
import SurveyResponses from "@components/SurveyResponses";
import Icon from "@components/text/Icon";
import LargeText from "@components/text/LargeText";
import Tooltip from "@components/Tooltip";
import ExternalLink from "@components/typography/ExternalLink";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useMyAdmin from "@hooks/useMyAdmin";
import useMyExpert from "@hooks/useMyExpert";
import { TimePeriodEnum } from "@typing/Enums";
import {
  ExpertGlobalRoleEnum,
  GetExpertOverviewDocument,
  GetExpertOverviewQuery,
  GetExpertOverviewQueryVariables
} from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";
import { calculateDatesFromTimePeriod } from "@utils/modelUtils";
import { minutesToHoursAndMinutes } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertKPIsFilters>["filters"];

type ActivityKindGroup =
  | "carePlan"
  | "carePlanUpdates"
  | "concierge"
  | "consult"
  | "documentation"
  | "messaging"
  | "resource";

type ActivityKindGroups = {
  billable: ActivityKindGroup[];
  nonBillable: ActivityKindGroup[];
};

const SurveyResponseRow = ({ label, value }: { label: number; value: number }) => (
  <div className={styles.scoreItem}>
    <IonText>{label}</IonText>
    <Icon color="sky" icon="star" spacingEnd spacingStart />
    <IonProgressBar className={styles.progressBar} value={value / 100} />
    <IonText>
      {value.toFixed(2).toString()}
      <FormattedMessage id={"dictionary.punctuation.percent"} />
    </IonText>
  </div>
);

const TimeLogBarItem = ({
  color,
  count,
  indicatorMessage,
  marginRight = false,
  titleKey,
  total
}: {
  color: string;
  count: number;
  indicatorMessage?: string;
  marginRight?: boolean;
  titleKey?: string;
  total: number;
}) => {
  if (count) {
    return (
      <div
        className={`${marginRight ? styles.marginRight : ""} ${styles.horizontalBarItem} ${color} ${
          indicatorMessage ? styles.barItemIndicator : ""
        }`}
        style={{ width: `${(count / total) * 100}%` }}
      >
        {titleKey && (
          <div
            className={`${styles.tooltipTop} ${indicatorMessage ? styles.biggerToolTip : ""}`}
            data-test="copy-tooltip"
          >
            <div className={styles.tooltipMessage + " " + color}>
              <div className={styles.bold}>
                <FormattedMessage id={titleKey} />
              </div>
              <div>
                <FormattedMessage
                  id="pages.expert.dashboard.overview.nHours"
                  values={{ count: minutesToHoursAndMinutes({ minutes: count, roundRemainder: true }) }}
                />
              </div>
              {indicatorMessage && (
                <div className={styles.indicatorMessage}>
                  <IonText className={styles.messageText}>{indicatorMessage}</IonText>
                </div>
              )}
            </div>
          </div>
        )}
        {indicatorMessage && <Icon color="warning" icon="exclamationTriangle" />}
      </div>
    );
  }
  return null;
};
const ExpertKPIs = () => {
  const intl = useIntl();
  const location = useLocation();
  const myExpert = useMyExpert();
  const myAdmin = useMyAdmin();

  const [filters, setFilters] = useState<Filters>({
    endDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).endDate,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
    startDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).startDate,
    timePeriod: TimePeriodEnum.SEVEN_DAYS
  });

  const [getExpertOverview, getExpertOverviewQuery] = useLazyQuery<
    GetExpertOverviewQuery,
    GetExpertOverviewQueryVariables
  >(GetExpertOverviewDocument);

  useSubscribeToJourneys(
    () => {
      getExpertOverviewQuery.refetch();
    },
    { skip: !myExpert }
  );

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    if (myExpert || myAdmin) {
      getExpertOverview({
        variables: {
          endDate: dates.endDate,
          expertId: filters.expertId,
          startDate: dates.startDate
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, getExpertOverview, myExpert]);

  const consultsByPosition = useMemo(() => {
    if (getExpertOverviewQuery.data?.expertOverview) {
      const initial = getExpertOverviewQuery.data.expertOverview.initialConsults;
      const second = getExpertOverviewQuery.data.expertOverview.secondConsults;
      const rest = getExpertOverviewQuery.data.expertOverview.thirdPlusConsults;

      const total = initial + second + rest;
      return {
        initial: { count: initial, percentage: (initial / total) * 100 },
        rest: { count: rest, percentage: (rest / total) * 100 },
        second: { count: second, percentage: (second / total) * 100 },
        total
      };
    }
  }, [getExpertOverviewQuery.data?.expertOverview]);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const consultHoursAndMinutes = getExpertOverviewQuery.data
    ? minutesToHoursAndMinutes({
        minutes:
          getExpertOverviewQuery.data.expertOverview.consultsFifteenMinutes * 15 +
          getExpertOverviewQuery.data.expertOverview.consultsThirtyMinutes * 30 +
          getExpertOverviewQuery.data.expertOverview.consultsOneHour * 60,
        roundRemainder: true
      })
    : null;

  const consultTimeAgainstLoggedTime = useMemo(() => {
    if (getExpertOverviewQuery.data) {
      const consultMinutes =
        getExpertOverviewQuery.data.expertOverview.consultsFifteenMinutes * 15 +
        getExpertOverviewQuery.data.expertOverview.consultsThirtyMinutes * 30 +
        getExpertOverviewQuery.data.expertOverview.consultsOneHour * 60;

      return consultMinutes - getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes.consult;
    }

    return 0;
  }, [getExpertOverviewQuery.data]);

  const activityKindGroups: ActivityKindGroups = {
    billable: ["carePlan", "carePlanUpdates", "resource", "consult", "documentation", "concierge", "messaging"],
    nonBillable: ["carePlan", "carePlanUpdates", "resource", "documentation", "concierge", "messaging"]
  };

  const otherCount = useMemo(() => {
    const billableData = getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes;
    const nonBillableData = getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes;
    if (!billableData || !nonBillableData) return { billable: 0, nonBillable: 0 };

    const billableTotal = activityKindGroups.billable.reduce((acc, curr) => acc + billableData[curr], 0);

    const nonBillableTotal = activityKindGroups.nonBillable.reduce((acc, curr) => acc + nonBillableData[curr], 0);

    return {
      billable: billableData.total - billableTotal,
      nonBillable: nonBillableData.total - nonBillableTotal
    };
  }, [
    activityKindGroups.billable,
    activityKindGroups.nonBillable,
    getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes,
    getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes
  ]);

  const calculateMarginRight = (group: ActivityKindGroup, billable: boolean) => {
    if (!getExpertOverviewQuery.data?.expertOverview) return false;

    const items = billable ? activityKindGroups.billable : activityKindGroups.nonBillable;
    const data = billable
      ? getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes
      : getExpertOverviewQuery.data.expertOverview.activityKindNonBillableMinutes;

    const index = items.indexOf(group);

    const itemsToRight = items.slice(index + 1);
    const other = billable ? otherCount.billable : otherCount.nonBillable;

    if (other > 0) return true;

    return itemsToRight.some(item => data[item] > 0);
  };

  const colorForIndex = (index: number | null, billable: boolean) => {
    switch (index) {
      case -1:
        return styles.emptyColor;
      case 0:
        return styles.sky;
      case 1:
        return styles.middlePurple;
      case 2:
        return styles.purple;
      case 3:
        return billable ? styles.green : styles.teal;
      case 4:
        return billable ? styles.teal : styles.lighterPurple;
      case 5:
        return billable ? styles.lighterPurple : styles.light;
      case 6:
        return styles.light;
      default:
        return styles.emptyColor;
    }
  };
  const translationForGroup = (group: ActivityKindGroup) => `pages.expert.dashboard.timeLogActivityKinds.${group}`;

  return (
    <>
      <div className={`${styles.filters} ${filters.timePeriod === TimePeriodEnum.CUSTOM ? styles.biggerFilters : ""}`}>
        <ExpertKPIsFilters filters={filters} onChange={handleFilterChange} />
      </div>

      <HandleQuery query={getExpertOverviewQuery}>
        {data => (
          <div>
            <div className={styles.boxGroup}>
              <div className={styles.boxShadow}>
                <Tooltip content={intl.formatMessage({ id: "pages.expert.dashboard.overview.consultsTip" })}>
                  {tooltipTriggerProps => (
                    <div className={styles.square} tabIndex={0} {...tooltipTriggerProps}>
                      <div className={styles.pieChartSquare}>
                        <div className={styles.pieChart}>
                          <PieChart
                            animate
                            background="#eaeaee"
                            data={[
                              { color: "#2e0f8b", value: data.expertOverview.consultsFifteenMinutes },
                              { color: "#4dbad3", value: data.expertOverview.consultsThirtyMinutes },
                              { color: "#b1a9ff", value: data.expertOverview.consultsOneHour }
                            ]}
                            lineWidth={20}
                          />
                          <div className={styles.total}>
                            <div>
                              <IonText className={styles.bigText}>
                                {data.expertOverview.consultsFifteenMinutes +
                                  data.expertOverview.consultsThirtyMinutes +
                                  data.expertOverview.consultsOneHour}
                              </IonText>
                            </div>
                            <IonText className={styles.smallText}>
                              <FormattedMessage id="pages.expert.dashboard.overview.consults" />
                            </IonText>
                          </div>
                        </div>
                        <ChartKey
                          items={[
                            {
                              color: styles.purple,
                              count: data.expertOverview.consultsFifteenMinutes,
                              key: intl.formatMessage({ id: "pages.expert.dashboard.overview.fifteenMinutes" })
                            },
                            {
                              color: styles.teal,
                              count: data.expertOverview.consultsThirtyMinutes,
                              key: intl.formatMessage({ id: "pages.expert.dashboard.overview.thirtyMinutes" })
                            },
                            {
                              color: styles.light,
                              count: data.expertOverview.consultsOneHour,
                              key: intl.formatMessage({ id: "pages.expert.dashboard.overview.oneHour" })
                            }
                          ]}
                        />
                      </div>
                      <div className={styles.hours}>
                        <FormattedMessage
                          id="pages.expert.dashboard.hours"
                          values={{ value: consultHoursAndMinutes }}
                        />
                      </div>
                      <div className={styles.boxTitle}>
                        <FormattedMessage id="pages.expert.dashboard.overview.consultsCount" />
                      </div>
                      {myExpert && (
                        <div className={styles.viewDetails}>
                          <InternalLink to={`/expert/dashboard/consults/duration${location.search}`}>
                            <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                          </InternalLink>
                        </div>
                      )}
                    </div>
                  )}
                </Tooltip>
              </div>
              <div className={styles.boxShadow}>
                <Tooltip content={intl.formatMessage({ id: "pages.expert.dashboard.overview.bookedConsultsTip" })}>
                  {tooltipTriggerProps => (
                    <div className={styles.square} tabIndex={0} {...tooltipTriggerProps}>
                      {consultsByPosition && (
                        <>
                          <div className={styles.barChart}>
                            <div className={styles.bar}>
                              {consultsByPosition.total > 0 ? (
                                <>
                                  <div
                                    className={styles.barItem + " " + styles.purple}
                                    style={{ height: consultsByPosition.initial.percentage.toString() + "px" }}
                                  />
                                  <div
                                    className={`${styles.barItem} ${styles.teal} ${
                                      consultsByPosition.initial.count ? styles.marginTop : ""
                                    } ${consultsByPosition.rest.count ? styles.marginBottom : ""} ${
                                      !consultsByPosition.rest.count && !consultsByPosition.second.count
                                        ? styles.noMargin
                                        : ""
                                    }`}
                                    style={{ height: consultsByPosition.second.percentage.toString() + "px" }}
                                  />
                                  <div
                                    className={styles.barItem + " " + styles.light}
                                    style={{ height: consultsByPosition.rest.percentage.toString() + "px" }}
                                  />
                                </>
                              ) : (
                                <div className={styles.barItem + " " + styles.empty} />
                              )}
                            </div>
                            <ChartKey
                              items={[
                                {
                                  color: styles.purple,
                                  count: consultsByPosition.initial.count,
                                  key: intl.formatMessage({ id: "pages.expert.dashboard.overview.initialConsult" })
                                },
                                {
                                  color: styles.teal,
                                  count: consultsByPosition.second.count,
                                  key: intl.formatMessage({ id: "pages.expert.dashboard.overview.secondConsult" })
                                },
                                {
                                  color: styles.light,
                                  count: consultsByPosition.rest.count,
                                  key: intl.formatMessage({ id: "pages.expert.dashboard.overview.restConsult" })
                                }
                              ]}
                            />
                          </div>

                          <div className={styles.boxTitle}>
                            <FormattedMessage id="pages.expert.dashboard.overview.bookedConsultsCount" />
                          </div>
                          {myExpert && (
                            <div className={styles.viewDetails}>
                              <InternalLink to={`/expert/dashboard/consults/position${location.search}`}>
                                <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                              </InternalLink>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Tooltip>
              </div>
              <div className={styles.boxShadow}>
                <div className={styles.square}>
                  <div className={styles.textCenter}>
                    <Icon color="primary-500" icon="users" size="large" />
                    <div className={styles.bigTextPurple}>{data.expertOverview.newClients}</div>
                    <div className={styles.smallText}>
                      <FormattedMessage id="pages.expert.dashboard.overview.newClientsText" />
                    </div>
                  </div>
                  <div className={styles.boxTitle}>
                    <FormattedMessage id="pages.expert.dashboard.overview.newSignUps" />
                  </div>
                  {myExpert && (
                    <div className={styles.viewDetails}>
                      <InternalLink to={`/expert/dashboard/new-signups${location.search}`}>
                        <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                      </InternalLink>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.boxShadow}>
                <Tooltip content={intl.formatMessage({ id: "pages.expert.dashboard.overview.averageHoursTip" })}>
                  {tooltipTriggerProps => (
                    <div className={styles.square} tabIndex={0} {...tooltipTriggerProps}>
                      <div className={styles.textCenter}>
                        <Icon color="primary-500" icon="repeat" size="large" />
                        <div className={styles.bigTextPurple}>
                          {minutesToHoursAndMinutes({
                            minutes: data.expertOverview.averageMinutesUsed,
                            roundRemainder: true
                          })}
                        </div>
                        <div className={styles.smallText}>
                          <FormattedMessage id="pages.expert.dashboard.overview.hours" />
                        </div>
                      </div>
                      <div className={styles.boxTitle}>
                        <FormattedMessage id="pages.expert.dashboard.overview.averageHours" />
                      </div>
                    </div>
                  )}
                </Tooltip>
              </div>
            </div>
            <div className="ion-margin-vertical">
              <LargeText className={styles.headerTitle}>
                <FormattedMessage id="pages.expert.dashboard.expertMetrics" />
              </LargeText>
            </div>
            <div className={styles.boxGroup}>
              <div className={styles.boxShadow}>
                <Tooltip
                  content={intl.formatMessage({
                    id: "pages.expert.dashboard.overview.carePlanTurnAroundTimeDescription"
                  })}
                >
                  {tooltipTriggerProps => (
                    <div className={`${styles.square} ${styles.carePlanTaT}`} tabIndex={0} {...tooltipTriggerProps}>
                      <div className={styles.textCenter}>
                        <Icon color="primary-500" icon="clock" size="large" />
                        <div className={styles.bigTextPurple}>
                          {minutesToHoursAndMinutes({
                            minutes: data.expertOverview.carePlanTurnAroundTime / 60,
                            roundRemainder: true
                          })}
                        </div>
                        <div className={styles.smallText}>
                          <FormattedMessage id="pages.expert.dashboard.overview.hours" />
                        </div>
                      </div>
                      <div className={styles.boxTitle}>
                        <FormattedMessage id="pages.expert.dashboard.overview.carePlanTurnAroundTime" />
                      </div>
                      {myExpert && (
                        <div className={styles.viewDetails}>
                          <InternalLink to={`/expert/dashboard/cp-turn-around-time${location.search}`}>
                            <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                          </InternalLink>
                        </div>
                      )}
                    </div>
                  )}
                </Tooltip>
              </div>
              <div className={styles.boxShadow}>
                <div className={styles.rectangle}>
                  <div className={styles.rectangleSpacing}>
                    <div className={styles.timeLogOverview}>
                      <div>
                        <FormattedMessage
                          id="pages.expert.dashboard.overview.nHours"
                          values={{
                            count: minutesToHoursAndMinutes({
                              minutes: data.expertOverview.activityKindBillableMinutes.total,
                              roundRemainder: true
                            })
                          }}
                        />
                      </div>
                      <div className="bold">
                        <FormattedMessage id="pages.expert.dashboard.overview.billable" />
                      </div>
                      {myExpert && (
                        <div className={styles.viewMoreTL}>
                          <InternalLink to={`/expert/dashboard/time-logs/billable${location.search}`}>
                            <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                          </InternalLink>
                        </div>
                      )}
                    </div>
                    <div className={styles.horizontalBarChart}>
                      {data.expertOverview.activityKindBillableMinutes.total === 0 && (
                        <TimeLogBarItem color={colorForIndex(null, true)} count={100} total={100} />
                      )}
                      {activityKindGroups.billable.map((group, index) => (
                        <TimeLogBarItem
                          color={colorForIndex(index, true)}
                          count={data.expertOverview.activityKindBillableMinutes[group]}
                          indicatorMessage={
                            group === "consult" && consultTimeAgainstLoggedTime > 0
                              ? intl.formatMessage(
                                  {
                                    id: "pages.expert.dashboard.consults.noMatch"
                                  },
                                  {
                                    count: minutesToHoursAndMinutes({
                                      minutes: consultTimeAgainstLoggedTime,
                                      roundRemainder: true
                                    })
                                  }
                                )
                              : undefined
                          }
                          key={group}
                          marginRight={calculateMarginRight(group, true)}
                          titleKey={translationForGroup(group)}
                          total={data.expertOverview.activityKindBillableMinutes.total}
                        />
                      ))}
                      <TimeLogBarItem
                        color={styles.grey}
                        count={otherCount.billable}
                        titleKey="pages.expert.dashboard.timeLogActivityKinds.other"
                        total={data.expertOverview.activityKindBillableMinutes.total}
                      />
                    </div>
                    <div className={styles.keySpacing}>
                      <ChartKey
                        items={activityKindGroups.billable
                          .map((group, index) => ({
                            color: colorForIndex(index, true),
                            key: intl.formatMessage({ id: translationForGroup(group) })
                          }))
                          .concat([
                            {
                              color: styles.grey,
                              key: intl.formatMessage({ id: "pages.expert.dashboard.timeLogActivityKinds.other" })
                            }
                          ])}
                        row
                      />
                    </div>
                  </div>
                  <div className={styles.rectangleSpacing}>
                    <div className={styles.timeLogOverview}>
                      <div>
                        <FormattedMessage
                          id="pages.expert.dashboard.overview.nHours"
                          values={{
                            count: minutesToHoursAndMinutes({
                              minutes: data.expertOverview.activityKindNonBillableMinutes.total,
                              roundRemainder: true
                            })
                          }}
                        />
                      </div>
                      <div className="bold">
                        <FormattedMessage id="pages.expert.dashboard.overview.nonBillable" />
                      </div>
                      {myExpert && (
                        <div className={styles.viewMoreTL}>
                          <InternalLink to={`/expert/dashboard/time-logs/non-billable${location.search}`}>
                            <FormattedMessage id="pages.expert.dashboard.overview.viewDetails" />
                          </InternalLink>
                        </div>
                      )}
                    </div>
                    <div className={styles.horizontalBarChart}>
                      {data.expertOverview.activityKindNonBillableMinutes.total === 0 && (
                        <TimeLogBarItem color={colorForIndex(null, false)} count={100} total={100} />
                      )}
                      {activityKindGroups.nonBillable.map((group, index) => (
                        <TimeLogBarItem
                          color={colorForIndex(index, false)}
                          count={data.expertOverview.activityKindNonBillableMinutes[group]}
                          key={group}
                          marginRight={calculateMarginRight(group, false)}
                          titleKey={translationForGroup(group)}
                          total={data.expertOverview.activityKindNonBillableMinutes.total}
                        />
                      ))}
                      <TimeLogBarItem
                        color={styles.grey}
                        count={otherCount.nonBillable}
                        titleKey="pages.expert.dashboard.timeLogActivityKinds.other"
                        total={data.expertOverview.activityKindNonBillableMinutes.total}
                      />
                    </div>
                    <div className={styles.keySpacing}>
                      <ChartKey
                        items={activityKindGroups.nonBillable
                          .map((group, index) => ({
                            color: colorForIndex(index, false),
                            key: intl.formatMessage({ id: translationForGroup(group) })
                          }))
                          .concat([
                            {
                              color: styles.grey,
                              key: intl.formatMessage({ id: "pages.expert.dashboard.timeLogActivityKinds.other" })
                            }
                          ])}
                        row
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.boxShadow}>
                <div className={styles.rectangle}>
                  <div className={styles.surveyResponses}>
                    <div className={styles.ratings}>
                      <SurveyResponseRow label={5} value={data.expertOverview.surveyResponseData.fiveStarPercent} />
                      <SurveyResponseRow label={4} value={data.expertOverview.surveyResponseData.fourStarPercent} />
                      <SurveyResponseRow label={3} value={data.expertOverview.surveyResponseData.threeStarPercent} />
                      <SurveyResponseRow label={2} value={data.expertOverview.surveyResponseData.twoStarPercent} />
                      <SurveyResponseRow label={1} value={data.expertOverview.surveyResponseData.oneStarPercent} />
                    </div>
                    <div>
                      <div className={styles.bigTextTeal}>
                        {data.expertOverview.surveyResponseData.totalAverage.toFixed(2)}
                      </div>

                      <img className={styles.fiveStarImage} src={imageUrl("fiveStars")} />
                      <div
                        className={styles.filledInStars}
                        style={{
                          backgroundImage: `linear-gradient(to right,  #6ab7d0 0%,#6ab7d0 ${
                            (data.expertOverview.surveyResponseData.totalAverage / 5) * 100
                          }%,#eaeaee 0%,#eaeaee 100%)`,
                          width: 140
                        }}
                      />
                      <div className={styles.smallText + " ion-margin-top"}>
                        {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? (
                          <ExternalLink href="https://app.delighted.com/signin">
                            <FormattedMessage
                              id="pages.expert.dashboard.overview.basedOnResponses"
                              values={{ count: data.expertOverview.surveyResponseData.totalCount }}
                            />
                          </ExternalLink>
                        ) : (
                          <IonText>
                            <FormattedMessage
                              id="pages.expert.dashboard.overview.basedOnResponses"
                              values={{ count: data.expertOverview.surveyResponseData.totalCount }}
                            />
                          </IonText>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.boxTitle}>
                    <FormattedMessage id="pages.expert.dashboard.overview.surveyRatings" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </HandleQuery>
      <div className="ion-margin-top">
        <LargeText className={styles.headerTitle}>
          <FormattedMessage id="pages.expert.dashboard.boxHeadings.expertHighlights" />
        </LargeText>
      </div>
      <ExpertHighlightsTable filters={filters} />
      <div className={styles.responses}>
        <LargeText className={styles.headerTitle}>
          <FormattedMessage id="pages.expert.dashboard.boxHeadings.surveyResponses" />
        </LargeText>
      </div>
      <SurveyResponses filters={filters} />
    </>
  );
};

export default ExpertKPIs;
